import { useEffect, useRef } from 'react'
import { paidInPerson } from '../lib/storePayment'
import type { Order } from '../store/useStore'
import { useStore } from '../store/useStore'
import { useMyOrders } from './useMyOrders'
import { useI18n } from '../i18n/useI18n'
import { showOrderReadyBanner } from '../components/OrderReadyBanner'
import { playChime, ORDER_READY_TONES } from '../lib/chime'
import { displayOrderNumber } from '../lib/orderNumber'
import { ensurePushSubscription } from '../lib/webPush'
import { api } from '../services/api'
import { genreOf, usesReceiptWord } from '../lib/genre'

function tryVibrate(): void {
  try {
    // iOS Safari ignores this; Android/PWA respects it.
    navigator.vibrate?.([200, 100, 200])
  } catch {
    // ignore
  }
}

function tryBrowserNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return
    const n = new Notification(title, { body, tag: 'yokoso-order-ready', icon: '/icon-192.png' })
    // Auto-close after 8s so the OS doesn't stack them.
    setTimeout(() => n.close(), 8000)
  } catch {
    // ignore
  }
}

// #1216 受け取り案内の鮮度。ready から この時間を過ぎた案内は出さない —
// 「2日前に準備できました」は届いた時点でノイズであり、窓が広いほど誤爆リスクも
// 上がる (オーナー判断 2026-07-20)。当日 (JST) 判定も併用する。
const PICKUP_NOTICE_WINDOW_MS = 6 * 60 * 60 * 1000

function isSameJstDay(iso: string, now: Date): boolean {
  const toJst = (d: Date) => new Date(d.getTime() + 9 * 60 * 60 * 1000)
  const a = toJst(new Date(iso))
  const b = toJst(now)
  return a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate()
}

/**
 * #1216 アプリを開いた時、未確認のまま受け取り可能になっている注文を知らせる。
 *
 * 条件は3つとも満たすものだけ (オーナー確定の設計):
 *   1. 未受け取り (`ready` のまま — picked_up / cancelled は対象外)
 *   2. サーバ側で未通知 (`readyNoticeShownAt` が無い) — localStorage と違い
 *      キャッシュ削除・機種変更でも過去分が復活しない
 *   3. 当日 (JST) かつ ready から 6 時間以内 — 受け取り案内は鮮度が全て
 *
 * 表示後は ack でサーバに既読を書き、以後リロードしても再発火しない。
 */
async function notifyPendingPickups(
  orders: Order[],
  stores: { id: string; name: string }[],
  t: (key: string, params?: Record<string, string>) => string,
): Promise<void> {
  const now = new Date()
  const pending = orders.filter((o) => {
    if (o.orderType !== 'takeout' || o.status !== 'ready') return false
    if (o.readyNoticeShownAt) return false
    const at = o.readyAt
    if (!at) return false
    return now.getTime() - new Date(at).getTime() <= PICKUP_NOTICE_WINDOW_MS
      && isSameJstDay(at, now)
  })
  if (!pending.length) return

  for (const o of pending) {
    const store = stores.find((s) => s.id === o.storeId)
    const orderId = displayOrderNumber(o)
    const title = t('notify.orderReady')
    const body = store?.name
      ? t('notify.orderReadyBody', { store: store.name, id: orderId })
      : t('notify.orderReadyBodyNoStore', { id: orderId })
    playChime(ORDER_READY_TONES)
    tryVibrate()
    tryBrowserNotification(title, body)
    showOrderReadyBanner({ title, body, orderId: o.id })
  }

  try {
    await api.ackReadyNotice(pending.map((o) => o.id))
  } catch {
    // 既読の記録に失敗しても案内は出せている。次回開いた時に再表示されるだけ
  }
}

/**
 * Fires three fallback channels when an order transitions to `served`:
 *   1. In-app banner (always runs, even in background tabs on wake-up)
 *   2. navigator.vibrate (foreground-only, ignored on iOS)
 *   3. Browser Notification (granted + backgrounded tabs)
 *
 * Mounted at App root so notifications fire regardless of which screen
 * the customer is on.
 */
export function useOrderReadyNotifier(): void {
  const { orders } = useMyOrders()
  const stores = useStore((s) => s.stores)
  const loggedIn = useStore((s) => s.isLoggedIn && !!s.userId)
  const { t, locale } = useI18n()

  // #1230 通知許可済みの端末では Web Push 購読をサーバへ同期しておく。
  // 未許可 / 未対応 / VAPID 未設定 / SW 無し (vite dev) では ensure が静かに
  // false を返すだけなので、App root で無条件に呼んでよい。locale 変更時は
  // 購読の locale を上書きし、push 文言の言語を追随させる。
  // ゲスト (#2140 で通知を出すようにした) は購読の登録先がアカウントに紐づかないので呼ばない。
  useEffect(() => {
    if (!loggedIn) return
    void ensurePushSubscription(locale)
  }, [locale, loggedIn])

  // Tracks the last-seen status for each order id. On the very first snapshot
  // we only seed the map — an order already `served` at mount time should
  // NOT trigger a notification (e.g. the user reloaded the page).
  const lastStatusRef = useRef<Map<string, Order['status']> | null>(null)
  // #968 現金会計の承認検知用に paid 状態も追跡。seed 済み (unpaid) の現金注文が
  // paid 化した瞬間だけ通知する。
  const lastPaidRef = useRef<Map<string, boolean>>(new Map())

  useEffect(() => {
    if (!orders.length) return

    if (lastStatusRef.current === null) {
      const seed = new Map<string, Order['status']>()
      const seedPaid = new Map<string, boolean>()
      for (const o of orders) { seed.set(o.id, o.status); seedPaid.set(o.id, !!o.paid) }
      lastStatusRef.current = seed
      lastPaidRef.current = seedPaid
      // #1216 アプリを閉じている間に受け取り可能になっていた注文を拾う。
      // 従来は seed のみで通知せず (リロードでの誤発火防止) 、閉じていた客は
      // 気づけなかった。既読はサーバ側フラグで管理するのでキャッシュ削除でも
      // 過去分は再通知されない。
      void notifyPendingPickups(orders, stores, t)
      return
    }

    const prev = lastStatusRef.current
    const prevPaid = lastPaidRef.current
    // #968 現金会計の承認 (unpaid→paid, method=cash) を店舗ごとに合算。卓一括等で
    // 複数注文が同時に paid 化しても店舗ごと1通知にまとめる。
    // #1397 締められた注文 ID も併せて持つ (レシート導線がサーバ側の現金取引を
    // 特定するのに使う — client は現金取引の ID を知らない)。
    const cashPaidByStore = new Map<string, { amount: number; orderIds: string[] }>()

    for (const o of orders) {
      const before = prev.get(o.id)
      // #956 (管理番号_007): never notify for an already-paid order. After
      // checkout the table is closed; a late 提供済み tap (or any → served
      // transition on a settled bill) must not push a "ready" notification to
      // the customer who has already paid and left.
      // #1213 takeout は先払いが正常なので paid ガードの対象外。受け取り可能
      // (`ready`) への遷移で通知する。dine-in は従来どおり `served` かつ未払いのみ。
      const isReadyTransition =
        o.orderType === 'takeout'
          ? o.status === 'ready' && before && before !== 'ready'
          : o.status === 'served' && before && before !== 'served' && !o.paid
      if (isReadyTransition) {
        const store = stores.find((s) => s.id === o.storeId)
        // 一覧に出ない店 (unlisted) は stores に無いので、注文に残した店名を使う
        const storeName = store?.name || o.storeName || ''
        const orderId = displayOrderNumber(o)
        const title = t('notify.orderReady')
        const body = storeName
          ? t('notify.orderReadyBody', { store: storeName, id: orderId })
          : t('notify.orderReadyBodyNoStore', { id: orderId })

        playChime(ORDER_READY_TONES)
        tryVibrate()
        tryBrowserNotification(title, body)
        showOrderReadyBanner({ title, body, orderId: o.id })
      }

      // #968 現金会計の承認: 店舗がレジで受領を確定すると paid=true になる。
      // seed 済みで unpaid だった現金注文が paid 化したものだけ拾う。
      const beforePaid = prevPaid.get(o.id)
      // #2162 お店の決済端末で受けた会計も、レジで受領が確定したもの (現金と同じ)
      if (paidInPerson(o.paymentMethod) && beforePaid === false && o.paid) {
        const cur = cashPaidByStore.get(o.storeId) || { amount: 0, orderIds: [] }
        cur.amount += o.totalAmount
        cur.orderIds.push(o.id)
        cashPaidByStore.set(o.storeId, cur)
      }

      prev.set(o.id, o.status)
      prevPaid.set(o.id, !!o.paid)
    }

    // 店舗ごとに1回、現金会計完了バナー。CTA でレシート＋レビュー＋シェアへ。
    for (const [storeId, paid] of cashPaidByStore) {
      const store = stores.find((s) => s.id === storeId)
      const storeName = store?.name || ''
      const title = t('notify.cashPaid')
      const body = storeName
        ? t(usesReceiptWord(store ? genreOf(store) : undefined) ? 'notify.cashPaidBody' : 'notify.cashPaidBodyStatement', { store: storeName })
        : t('notify.cashPaidBodyNoStore')

      playChime(ORDER_READY_TONES)
      tryVibrate()
      tryBrowserNotification(title, body)
      showOrderReadyBanner({
        title,
        body,
        orderId: '',
        review: { storeId, amount: paid.amount, orderIds: paid.orderIds },
      })
    }

    // Drop entries for orders that disappeared (cancelled + filtered out, etc.)
    for (const id of prev.keys()) {
      if (!orders.find((o) => o.id === id)) { prev.delete(id); prevPaid.delete(id) }
    }
  }, [orders, stores, t])
}
